import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { productPath } from "@/lib/url-slugs";
import { useSiteSettings } from "@/hooks/use-site-settings";
import type { Product } from "@/types/product";
import { ShoppingCart, Rocket, Globe } from "lucide-react";
import { optimizeImageUrl, responsiveSrcSet, CARD_WIDTHS } from "@/lib/image-utils";
import AvailabilityInquiryModal from "@/components/products/AvailabilityInquiryModal";
import UsedByProBadge from "@/components/products/UsedByProBadge";
import type { PlayerRef } from "@/hooks/use-player-products";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { logProductActivity } from "@/lib/product-activity";
import { isStarvieProduct, isRestrictedCountry, STARVIE_BLOCK_MESSAGE_EN, STARVIE_BLOCK_MESSAGE_LV } from "@/lib/starvie-restrictions";
import { useVisitorCountry } from "@/hooks/use-visitor-country";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getFeaturedRacketTheme } from "@/lib/featured-rackets";
import { getSignaturePlayer } from "@/lib/signature-rackets";


interface ProductCardProps {
  product: Product;
  hideOverlays?: boolean;
  hideTopBadges?: boolean;
  /** Above-the-fold cards should set this so the LCP image loads eagerly. */
  priority?: boolean;
  /** Pro players who use this product — renders a "USED BY PRO" pill. */
  players?: PlayerRef[];
  /** Enlarged presentation used by the homepage promotional section. */
  showcase?: boolean;
}

const ProductCard = ({ product, hideOverlays = false, hideTopBadges = false, priority = false, players, showcase = false }: ProductCardProps) => {
  const { addToCart } = useCart();
  const { t, lang } = useLanguage();
  const visitorCountry = useVisitorCountry();
  const isStarvieBlocked = isStarvieProduct(product) && isRestrictedCountry(visitorCountry);
  const blockMsg = lang === "lv" ? STARVIE_BLOCK_MESSAGE_LV : STARVIE_BLOCK_MESSAGE_EN;
  const detailHref = productPath(lang, product.slug);
  const { data: settings } = useSiteSettings();
  const primaryImage = product.images?.[0];
  const hoverImage = product.images?.[2] || null;
  const availabilitySource = product.availabilitySource || (product.stock > 0 ? "local" : ((product as any).stockSpain ?? 0) > 0 ? "europe" : "contact");
  const isOutOfStock = availabilitySource === "contact";
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoverImageRequested, setHoverImageRequested] = useState(false);
  const [primaryLoaded, setPrimaryLoaded] = useState(false);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const featuredTheme = getFeaturedRacketTheme(product.sku);
  const signaturePlayer = getSignaturePlayer(product.sku);

  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const primaryImgRef = React.useRef<HTMLImageElement>(null);

  // If the image is served from cache, the `load` event may fire before React
  // attaches its onLoad handler. Detect that case on mount so we don't leave
  // the image stuck at opacity-0.
  React.useEffect(() => {
    if (primaryImgRef.current?.complete && primaryImgRef.current.naturalWidth > 0) {
      setPrimaryLoaded(true);
    }
  }, [primaryImage]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hoverImage || !imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2; // -1 to 1
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setParallax({ x: x * 12, y: y * 12 }); // max 12px shift
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setParallax({ x: 0, y: 0 });
  };

  const discountPercent =
    product.compareAtPrice && product.compareAtPrice > product.price
      ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
      : 0;

  const promoEnabled = settings?.promo_badge_enabled === "true";
  const promoText = settings?.promo_badge_text || "";

  const sameDayDelivery = (product as any).sameDayDelivery === true;




  return (
    <>
    <div className={cn(
      "group bg-card rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden h-full flex flex-col",
      featuredTheme === "gold" && "border-premium-gold/70 shadow-premium-gold hover:border-premium-gold",
      featuredTheme === "wine" && "border-premium-wine/70 shadow-premium-wine hover:border-premium-wine",
      showcase && "md:grid md:grid-cols-[minmax(0,1.12fr)_minmax(220px,0.88fr)]"
    )}>
        <Link
          to={detailHref}
          onClick={() => logProductActivity({
            action: "click",
            productId: product.id,
            productName: product.name,
            productSlug: product.slug,
            productSku: (product as any).sku ?? null,
            source: "card",
          })}
        >
          <div
            ref={imageContainerRef}
            className={cn(
              "aspect-square flex items-center justify-center overflow-hidden relative w-full",
              !featuredTheme && "bg-white",
              featuredTheme === "gold" && "bg-premium-gold-soft",
              featuredTheme === "wine" && "bg-premium-wine-soft",
              showcase && "md:h-full md:min-h-[360px] md:aspect-auto"
            )}
            onMouseEnter={() => {
              if (hoverImage) {
                setIsHovered(true);
                setHoverImageRequested(true);
              }
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {primaryImage ? (
              <>
                <img
                  ref={primaryImgRef}
                  src={optimizeImageUrl(primaryImage, { width: 300 })}
                  srcSet={responsiveSrcSet(primaryImage, CARD_WIDTHS)}
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 260px"
                  alt={product.name}
                  width={400}
                  height={400}
                  loading={priority ? "eager" : "lazy"}
                  decoding={priority ? "sync" : "async"}
                  fetchPriority={priority ? "high" : "auto"}
                  onLoad={() => setPrimaryLoaded(true)}
                  className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 ease-in-out group-hover:scale-105"
                />
                {hoverImage && hoverImageRequested && (
                  <>
                    <div
                      className="absolute inset-0 bg-white"
                      style={{
                        clipPath: isHovered ? "inset(0 0 0 0)" : "inset(0 0 0 100%)",
                        transition: "clip-path 0.25s ease-in-out",
                      }}
                    />
                    <img
                      src={optimizeImageUrl(hoverImage, { width: 600 })}
                      alt={`${product.name} - hover`}
                      width={400}
                      height={400}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-contain"
                      style={{
                        clipPath: isHovered ? "inset(0 0 0 0)" : "inset(0 0 0 100%)",
                        transform: isHovered
                          ? `scale(1.08) translate(${parallax.x}px, ${parallax.y}px)`
                          : "scale(1)",
                        transition: "clip-path 0.25s ease-in-out, transform 0.15s ease-out",
                      }}
                    />
                  </>
                )}
              </>

            ) : (
              <span className="text-sm font-medium text-muted-foreground group-hover:scale-105 transition-transform">{product.brand}</span>
            )}

            {!hideOverlays && (
              <>
                {featuredTheme && (
                  <span className={cn(
                    "absolute bottom-2 right-2 z-20 rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
                    featuredTheme === "gold" ? "bg-premium-gold text-premium-gold-foreground" : "bg-premium-wine text-premium-wine-foreground"
                  )}>
                    {lang === "lv" ? "Signature rakete" : "Signature racket"}
                  </span>
                )}

                {/* Badges - top left: SALE + rocket */}
                {!hideTopBadges && (
                  <div className="absolute top-2 left-2 z-20 flex flex-col gap-1">
                    {discountPercent > 0 && (
                      <span className="bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded">
                        -{discountPercent}%
                      </span>
                    )}
                    {sameDayDelivery && !isOutOfStock && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded flex items-center justify-center cursor-default">
                              <Rocket size={14} />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="text-center leading-tight">Piegāde šodien<br />(tikai Rīgā)</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}

                {/* Promo badge - top right */}
                {promoEnabled && promoText && (
                  <span className="absolute top-2 right-2 z-20 bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 py-0.5 rounded leading-tight max-w-[100px] text-center">
                    {promoText}
                  </span>
                )}

                {/* FIP Approved badge - bottom left */}
                {(
                  product.slug === "siux-neo-speed-1" ||
                  product.slug === "siux-neo-speed-24-gab" ||
                  (product.product_type === "balls" && !/premium\s*pro/i.test(product.name))
                ) && (
                  <span className="absolute bottom-2 left-2 z-20 bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded leading-tight">
                    FIP Approved
                  </span>
                )}

                {isOutOfStock && (
                  <div className="absolute inset-0 z-10 bg-background/50 flex items-center justify-center">
                    <span className="border-2 border-destructive text-destructive font-display font-bold text-[10px] md:text-xs px-2 py-1 rounded uppercase tracking-wide bg-background/80 text-center leading-tight">
                      {t("availability.contact")}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </Link>
        <div className={cn("p-3 md:p-4 flex flex-col flex-1", showcase && "md:p-6 md:justify-center")}>
          <Link to={detailHref} className="flex-1 min-h-0">
            <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>
            {signaturePlayer && (
              <p className={cn(
                "text-xs font-semibold mb-1",
                featuredTheme === "gold" ? "text-premium-gold" : "text-premium-wine"
              )}>
                {signaturePlayer}
              </p>
            )}
            <h3 className="font-display font-semibold text-sm md:text-base leading-tight mb-2 group-hover:text-primary transition-colors line-clamp-2">{product.name}</h3>
          </Link>

          <div className="mt-auto">
            {!hideOverlays && !isOutOfStock && (
              <div className="flex flex-col gap-1 mb-2">
                {availabilitySource === "europe" ? (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <Globe size={12} />
                    {t("availability.europe")}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {t("availability.riga")}
                  </p>
                )}


              </div>
            )}
            {players && players.length > 0 && (
              <div className="mb-2">
                <UsedByProBadge players={players} variant="card" />
              </div>
            )}
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display font-bold text-price">€{product.price.toFixed(2)}</span>
              {product.compareAtPrice && (
                <span className="text-xs text-muted-foreground line-through">€{product.compareAtPrice.toFixed(2)}</span>
              )}
            </div>
            {isStarvieBlocked ? (
              <Button size="sm" className="w-full gap-2 font-display" variant="outline" disabled title={blockMsg}>
                {blockMsg}
              </Button>
            ) : isOutOfStock ? (
              <Button size="sm" className="w-full gap-2 font-display" variant="outline" onClick={() => setInquiryOpen(true)}>
                {t("availability.contact")}
              </Button>
            ) : (
              <Button size="sm" className="w-full gap-2 font-display" onClick={() => addToCart(product)}>
                <ShoppingCart size={14} /> {t("product.add_to_cart")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AvailabilityInquiryModal product={product} open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </>
  );
};

export default React.memo(ProductCard, (prev, next) =>
  prev.product.id === next.product.id &&
  prev.product.price === next.product.price &&
  prev.product.stock === next.product.stock &&
  (prev.product as any).stockSpain === (next.product as any).stockSpain &&
  (prev.product.images?.[0] ?? "") === (next.product.images?.[0] ?? "") &&
  (prev.product.images?.length ?? 0) === (next.product.images?.length ?? 0) &&
  prev.hideOverlays === next.hideOverlays &&
  prev.hideTopBadges === next.hideTopBadges &&
  prev.priority === next.priority &&
  prev.showcase === next.showcase &&
  (prev.players?.length ?? 0) === (next.players?.length ?? 0) &&
  (prev.players?.[0]?.player_id ?? "") === (next.players?.[0]?.player_id ?? "")
);

